import { Modal, Body1, Link } from '@tumelo/designsystem'
import { useSessionStorage } from '@tumelo/react'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useState } from 'react'
import { useFetchListSchemes } from '../../application/hooks/useFetchListSchemes'
import { usePrivileges } from '../../application/PrivilegesContext'
import { slugToSchemeName, schemeNameToSlug } from '../../utils/getCurrentSlug'
import { SchemeRadioButtons } from './SchemeRadioButtons'

const isValidUUID = (value: string | string[] | undefined): boolean => {
  if (!value || Array.isArray(value)) return false
  const uuidRegex = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
  return uuidRegex.test(value)
}

type SchemeGuardProps = PropsWithChildren

export const SchemeGuard: React.FC<SchemeGuardProps> = ({ children }) => {
  const router = useRouter()
  const privileges = usePrivileges()
  const { data: schemesResponse } = useFetchListSchemes()
  const [selectedSchemeName, setSelectedSchemeName] = useState('')
  const [hasSeenGuardThisSession, setHasSeenGuardThisSession] = useSessionStorage('schemeGuardSeen', false)

  // Check if both habitatID and schemeID are proper UIDs
  const { habitatID, schemeID } = router.query
  const hasValidUIDs = isValidUUID(habitatID) && isValidUUID(schemeID)

  // Only protect scheme-specific routes (habitatID/schemeID pattern)
  const isSchemeSpecificRoute = router.asPath.split('/').filter(Boolean).length >= 2 && hasValidUIDs

  // If not a scheme-specific route or UIDs are not valid, render children normally
  if (!isSchemeSpecificRoute || !hasValidUIDs) {
    return <>{children}</>
  }

  // If user has already seen the guard this session, just render the children
  if (hasSeenGuardThisSession) {
    return <>{children}</>
  }

  // Don't render anything while loading or if no router query yet
  if (!router.isReady || !privileges || !schemesResponse?.schemes) {
    return null
  }

  const currentSchemeName = slugToSchemeName(router)
  const availableSchemes = schemesResponse.schemes

  const handleSchemeChange = (schemeName: string) => {
    setSelectedSchemeName(schemeName)
  }

  const handleConfirm = () => {
    if (selectedSchemeName) {
      // Mark that user has seen the guard this session
      setHasSeenGuardThisSession(true)

      const newSlug = schemeNameToSlug(selectedSchemeName)

      // Replace UUIDs in current path with the new scheme slug
      const currentPath = router.asPath
      const pathParts = currentPath.split('/')

      // Replace the habitatID/schemeID portion with newSlug, preserve the rest
      if (pathParts.length >= 3) {
        const [, , , ...restParts] = pathParts
        const newPath = `/${newSlug}/${restParts.join('/')}`
        router.push(newPath)
      }
    }
  }

  // Check if user has access to any schemes at all
  const hasAnySchemeAccess = availableSchemes.length > 0

  if (!hasAnySchemeAccess) {
    return (
      <Modal
        open={true}
        onCloseCallback={() => {
          setHasSeenGuardThisSession(true)
          router.push('/')
        }}
        headerType="title"
        title="No schemes available"
        type="action"
        stickyButtons={[
          {
            label: 'Go to Home',
            variant: 'primary',
            onClick: () => {
              setHasSeenGuardThisSession(true)
              router.push('/')
            },
          },
        ]}
      >
        <Body1>
          You don&apos;t have access to any schemes. Please contact support at{' '}
          <Link colour="dark" href="mailto:support@tumelo.com">
            support@tumelo.com
          </Link>{' '}
          for assistance.
        </Body1>
      </Modal>
    )
  }

  // Check if user has permission for the current scheme
  const hasPermission = privileges.canViewMeetingsWithinSchemes?.[currentSchemeName] === true

  // If user has permission for current scheme, render the page
  if (hasPermission) {
    return <>{children}</>
  }

  // User doesn't have permission for current scheme, show scheme selector
  return (
    <Modal
      open={true}
      onCloseCallback={() => {
        setHasSeenGuardThisSession(true)
        router.push('/')
      }}
      headerType="title"
      title="Select a Scheme"
      type="action"
      stickyButtons={[
        { label: 'Confirm', variant: 'primary', onClick: handleConfirm, isDisabled: !selectedSchemeName },
      ]}
    >
      <Body1>You don&apos;t have access to this page. Please select a scheme you have access to:</Body1>
      <SchemeRadioButtons
        schemes={availableSchemes}
        selectedSchemeName={selectedSchemeName}
        onSchemeChange={handleSchemeChange}
      />
    </Modal>
  )
}
